
/**
 * @name OTP_REGEX
 * @description Regular expression for validating 6-digit OTP codes
 */
const OTP_REGEX = /^[0-9]{6}$/;

/**
 * @name PHONE_NUMBER_REGEX
 * @description Regular expression to validate phone numbers in various formats
 */
const PHONE_NUMBER_REGEX = /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/;

/**
 * @name EMAIL_REGEX
 * @description Regular expression to validate email addresses
 */
const EMAIL_REGEX = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

/**
 * @name NAME_REGEX
 * @description Regular expression to validate names (alphanumeric, starting with a letter)
 */
const NAME_REGEX = /^(?:[a-z]|[a-z]+[0-9])[a-z0-9]*$/i;

/**
 * @name FULL_NAME_REGEX
 * @description Regular expression to validate full names (letters, hyphens, and spaces only)
 */
const FULL_NAME_REGEX = /^[a-zA-Z\-\s]+$/;

/**
 * @name PASSWORD_REGEX
 * @description Regular expression to validate passwords
 * Requires: at least 8 characters, one uppercase, one lowercase, one number, one special character
 * Example: Admin@123
 */
const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*#?&^()_+\-=\[\]{};':"\\|,.<>\/])[A-Za-z\d@$!%*#?&^()_+\-=\[\]{};':"\\|,.<>\/]{8,}$/;

/**
 * @name OTP_WITH_SPACES_REGEX
 * @description Regular expression for validating OTP codes with spaces allowed
 */
const OTP_WITH_SPACES_REGEX = /^[0-9\s]+$/;

// Size constants
const VALIDATION_CONSTANTS = {
  /** Maximum length for full name */
  FULL_NAME_MAX_LENGTH: 512,
  
  /** Maximum length for email */
  EMAIL_MAX_LENGTH: 512,
  
  /** Minimum length for contact number */
  CONTACT_NUMBER_MIN_LENGTH: 10,
  
  /** Maximum length for contact number */
  CONTACT_NUMBER_MAX_LENGTH: 18,
  
  /** Minimum length for translation key */
  TRANSLATION_KEY_MIN_LENGTH: 3,
  
  /** Maximum length for translation key */
  TRANSLATION_KEY_MAX_LENGTH: 100,
  
  /** Minimum length for translation title */
  TRANSLATION_TITLE_MIN_LENGTH: 1,
  
  /** Maximum length for translation title */
  TRANSLATION_TITLE_MAX_LENGTH: 500,
};

/**
 * @name changeLanguageHandler
 * @description A function that changes the language of the application using the `i18n` instance. It triggers the `changeLanguage` method to switch between languages.
 * @param {Object} i18n - The i18n instance that handles language management.
 * @param {string} lang - The language code (e.g., 'en', 'fr', 'es') to switch to.
 * @returns {void}
 */
const changeLanguageHandler = (i18n, lang) => {
  i18n.changeLanguage(lang);
};

const imagePreviewFromik = (file) => {
  if (!file) {
    return "";
  }

  if (typeof file === "string") {
    return file;
  }

  return URL.createObjectURL(file);
};

const defaultValue = (option, value) => {
   if (typeof value === "string" || typeof value === "number") {
     return option ? option.find((option) => option.value === value) : "";
  } else if (Array.isArray(value)) {
     return option
       ? option.filter((option) => {
          return value?.includes(option.value.toString());
         })
       : "";
  } else {
    return "";
   }
 };

/**
 * @name onKeyPress
 * @description Prevents invalid key inputs in a numeric input field.
 * It allows only digits (0-9) and a single decimal point ('.').
 * If a non-numeric character is pressed or if a second decimal point is attempted, the event is prevented.
 * @param {React.KeyboardEvent} e - The keyboard event triggered on key press.
 * @returns {void}
 */
const onkeyPress = (e) => {
  if (
    !/[0-9.]/.test(e.key) ||
    (e.key === "." && e.currentTarget.value.includes("."))
  ) {
    e.preventDefault();
  }
};

const formatDays = (diffDays) => {
  if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    const days = diffDays % 7;

    return `${weeks > 0 ? weeks + " week" + (weeks > 1 ? "s" : "") : ""} ${
      days > 0 ? days + " day" + (days > 1 ? "s" : "") : ""
    }`.trim();
  } else {
    // months + days
    const months = Math.floor(diffDays / 30);
    const days = diffDays % 30;

    return `${months > 0 ? months + " month" + (months > 1 ? "s" : "") : ""} ${
      days > 0 ? days + " day" + (days > 1 ? "s" : "") : ""
    }`.trim();
  }
};

function formatLastUpdated(dateString) {
  const now = new Date();
  const past = new Date(dateString);
  const diffMs = now - past;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHour < 24) return `${diffHour}h ago`;

  if (diffDay === 1) {
    return `yesterday at ${past.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })}`;
  }

  return past.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}
export {
  changeLanguageHandler,
  imagePreviewFromik,
  defaultValue,
  onkeyPress,
  formatDays,
  formatLastUpdated,
  OTP_REGEX,
  PHONE_NUMBER_REGEX,
  EMAIL_REGEX,
  NAME_REGEX,
  FULL_NAME_REGEX,
  PASSWORD_REGEX,
  OTP_WITH_SPACES_REGEX,
  VALIDATION_CONSTANTS,
};
